<template>
  <InputWrapper
    v-slot="{ inputId }"
    :input-id="id"
    :label="t('tags.tags', 2)"
    class="tag-filter"
    optional
  >
    <TagMultiSelect
      :id="inputId"
      ref="tagMultiSelectRef"
      v-model="internalValue.tags"
      :loading="tagOptions.loading"
      :options="tagOptions.data"
      :allow-deletes="canDeleteTags"
      @delete="handleDelete"
    />
    <TagSubDomainDeleteModal
      v-model:visible="showTagSubdomainDeleteModal"
      :tags="tags.data"
      :selected-tag="selectedDeleteTag"
      @delete="onDelete"
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { Tag } from '@rygen/rygen-ui/model';

import { OrderSearchCriteria, ShipmentSearchCriteria } from '@/model';
import { useTagStore } from '@/stores/Tag';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import TagMultiSelect from '@rygen/rygen-ui/components/tag/TagMultiSelect.vue';
import TagSubDomainDeleteModal from '@rygen/rygen-ui/components/tag/TagSubDomainDeleteModal.vue';

const internalValue = defineModel({
  type: Object as PropType<OrderSearchCriteria | ShipmentSearchCriteria>,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
});
const { id } = toRefs(props);

const tagMultiSelectRef = useTemplateRef('tagMultiSelectRef');

const { t } = useI18n();
const { loadTags, tags, deleteTag, tagOptions } = useTagStore();
const toast = useToast();

const showTagSubdomainDeleteModal = ref(false);
const selectedDeleteTag: Ref<Tag> = ref(new Tag());

const canDeleteTags = computed((): boolean =>
  authorization.hasAuthority('tag', 'manage'),
);

const handleDelete = async (tag: string) => {
  const tagToDelete = tags.value.data?.find((t: Tag) => t.name === tag);
  selectedDeleteTag.value = tagToDelete!!;

  showTagSubdomainDeleteModal.value = true;
  tagMultiSelectRef.value?.hide();
};

const onDelete = async (tag: Tag) => {
  try {
    await deleteTag(tag);
    showTagSubdomainDeleteModal.value = false;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(`Error deleting tag ${tag}`, e);
    toast.addError({
      error: e,
      detail: t('errors.deletingParam', {
        param: t('tags.tags'),
      }),
    });
  }
};

onMounted(() => {
  loadTags();
});
</script>
